import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  IconButton,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Save as SaveIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { SourceDisplayConfig, customerNames } from '../../types/leadSourceRules';
import { api } from '../../helpers/apiUtils';

interface DisplayConfigEditorProps {
  selectedCustomer: string;
}

const DisplayConfigEditor: React.FC<DisplayConfigEditorProps> = ({ selectedCustomer }) => {
  const [configs, setConfigs] = useState<SourceDisplayConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalConfigs, setOriginalConfigs] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/lead-source-rules/display-config/${selectedCustomer}`);
      if (response.data?.success && response.data.data) {
        setConfigs(response.data.data);
        setOriginalConfigs(JSON.stringify(response.data.data));
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error loading display config:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Anzeige-Konfiguration',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    setHasChanges(JSON.stringify(configs) !== originalConfigs);
  }, [configs, originalConfigs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put(
        `/api/lead-source-rules/display-config/${selectedCustomer}`,
        configs,
      );
      if (response.data?.success) {
        setOriginalConfigs(JSON.stringify(configs));
        setHasChanges(false);
        setSnackbar({
          open: true,
          message: 'Konfiguration gespeichert',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error saving display config:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/api/lead-source-rules/display-config/sync');
      if (response.data?.success) {
        const added = response.data.data?.added || 0;
        setSnackbar({
          open: true,
          message: `Sync abgeschlossen: ${added} neue Quellen hinzugefügt`,
          severity: 'success',
        });
        await loadConfig();
      }
    } catch (error) {
      console.error('Error syncing display config:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Synchronisieren',
        severity: 'error',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (sourceKey: string) => {
    try {
      await api.delete(`/api/lead-source-rules/display-config/${selectedCustomer}/${sourceKey}`);
      setConfigs((prev) => prev.filter((c) => c.source_key !== sourceKey));
      setSnackbar({
        open: true,
        message: `${sourceKey} gelöscht`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting display config:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen',
        severity: 'error',
      });
    }
  };

  const updateConfig = (
    index: number,
    field: keyof SourceDisplayConfig,
    value: string | number | boolean | null,
  ) => {
    setConfigs((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= configs.length) return;

    setConfigs((prev) => {
      const updated = [...prev];
      const temp = updated[index];
      updated[index] = updated[newIndex];
      updated[newIndex] = temp;
      // Recalculate sort_order based on new positions
      return updated.map((c, i) => ({ ...c, sort_order: i }));
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Anzeige-Konfiguration: {customerNames[selectedCustomer] || selectedCustomer}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync aus Regeln
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            Speichern
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
          Ungespeicherte Änderungen
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 60,
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 140,
                }}
              >
                Source Key
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Anzeigename
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 120,
                }}
              >
                Farbe
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 80,
                }}
                align="center"
              >
                Sichtbar
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 100,
                }}
                align="center"
              >
                Sortierung
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 60,
                }}
                align="center"
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config, index) => (
              <TableRow
                key={config.source_key}
                sx={{
                  opacity: config.is_visible ? 1 : 0.5,
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
              >
                <TableCell>{index}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {config.source_key}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={config.display_name}
                    onChange={(e) => updateConfig(index, 'display_name', e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        backgroundColor: config.color || '#ccc',
                        border: '1px solid #999',
                        flexShrink: 0,
                      }}
                    />
                    <TextField
                      size="small"
                      value={config.color || ''}
                      onChange={(e) => updateConfig(index, 'color', e.target.value || null)}
                      placeholder="#hex"
                      variant="outlined"
                      sx={{
                        width: 90,
                        '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                      }}
                      inputProps={{
                        style: { fontFamily: 'monospace', fontSize: '0.8rem' },
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={config.is_visible}
                    onChange={(e) => updateConfig(index, 'is_visible', e.target.checked)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === configs.length - 1}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {config.source_key !== 'all' && config.source_key !== 'other' && (
                    <Tooltip title="Löschen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(config.source_key)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DisplayConfigEditor;
